import { customEvents } from "@/global/config/constants";
import { TeamConfig } from "@/global/hooks/useTeamConfig";
import {
  EventType,
  GameFilters,
  SectionName,
  DynamicSeatCount,
  OpponentType,
} from "@/global/store/data/types";
import { dispatchCustomEvent } from "@/global/utils/dispatchCustomEvent";

export function getApiPriceRange(
  current: GameFilters["priceRange"],
  next: { min: number | null; max: number | null }
) {
  if (next.min == null || next.max == null) return current;

  const nextMin = Number(Math.max(next.min, 1));
  const nextMax = Number(Math.max(next.max, 1));

  if (nextMin < nextMax) {
    return {
      data: { min: nextMin, max: nextMax },
      selected: [nextMin, nextMax],
    };
  } else if (nextMin == nextMax) {
    return {
      data: { min: 0, max: nextMax },
      selected: [0, nextMax],
    };
  } else return current;
}

export function calcNextPriceRange(
  current: GameFilters["priceRange"],
  next: { min: number | null; max: number | null }
) {
  if (next.min == null || next.max == null) return current;

  const nextMin = Number(Math.max(next.min, 1));
  const nextMax = Number(Math.max(next.max, 1));

  // Validate and set the new range
  let range = { min: 1, max: 1000 };
  if (nextMin < nextMax) {
    range.min = nextMin;
    range.max = nextMax;
  }

  // Validate and set the new selected values within the bounds of the new range
  let selected = [...current.selected];
  selected[0] = Math.max(Math.min(selected[0], range.max), range.min);
  selected[1] = Math.max(Math.min(selected[1], range.max), range.min);

  // Ensure that selected[0] is always <= selected[1]
  if (selected[0] > selected[1]) {
    [selected[0], selected[1]] = [selected[1], selected[0]];
  }

  // Ensure that selected[0] and selected[1] are not the same, unless it's intentional
  if (selected[0] === selected[1]) {
    if (selected[0] > range.min) {
      selected[0] = selected[0] - 1;
    } else if (selected[1] < range.max) {
      selected[1] = selected[1] + 1;
    }
  }

  return {
    data: range,
    selected,
  };
}

export function calcNextSectionsState(
  sectionsState: GameFilters["sections"],
  apiSections: SectionName[]
) {
  let newSelection = sectionsState?.selected;
  const selectedSectionName = sectionsState?.selected?.mapSectionName || "";

  const isIncludedInNewSections = apiSections
    .map((itm) => itm.mapSectionName)
    .includes(selectedSectionName);

  if (!isIncludedInNewSections) {
    newSelection = null;
  }
  return {
    data: apiSections,
    selected: newSelection,
  };
}

export function calcEventNextSeatCount(seatCount: number, event: EventType) {
  const { maxSeatLimit, seatsClaimed } = event;
  if (maxSeatLimit > seatsClaimed) {
    return Math.min(seatCount, maxSeatLimit - seatsClaimed);
  }
  return seatCount;
}

export function calcMaxSeatLimit(
  event: EventType | null,
  teamConfig: TeamConfig
) {
  const {
    purchaseSeatConfigs: { minSeatLimit },
  } = teamConfig;

  if (event) {
    const { maxSeatLimit, seatsClaimed } = event;
    return maxSeatLimit - seatsClaimed;
  }
  return minSeatLimit;
}

export function calcDynamicSeatCount(
  dynamicSeatCount: DynamicSeatCount[],
  maxSeatCount: number
) {
  const dynamicSeatCountArr = [...dynamicSeatCount];
  const totalSeatCount = dynamicSeatCount.reduce((acc, curr) => {
    return acc + curr.seatCount;
  }, 0);

  if (totalSeatCount <= maxSeatCount) {
    return dynamicSeatCountArr;
  }

  let overflowSeatCount = totalSeatCount - maxSeatCount;

  // Sort by orderIndex in descending order to reduce from highest first
  const sortedItems = [...dynamicSeatCountArr].sort(
    (a, b) => b.orderIndex - a.orderIndex
  );

  for (const item of sortedItems) {
    if (overflowSeatCount <= 0) break;

    const itemIndex = dynamicSeatCountArr.findIndex((i) => i.id === item.id);
    const currentItem = dynamicSeatCountArr[itemIndex];

    // Find the minimum orderIndex to ensure at least one item keeps 1 seat
    const minOrderIndex = Math.min(
      ...dynamicSeatCountArr.map((i) => i.orderIndex)
    );
    const isLowestIndex = currentItem.orderIndex === minOrderIndex;

    // For lowest orderIndex, keep minimum 1 seat, otherwise can reduce to 0
    const minSeats = isLowestIndex ? 1 : 0;
    const maxReduction = Math.max(0, currentItem.seatCount - minSeats);
    const actualReduction = Math.min(overflowSeatCount, maxReduction);

    if (actualReduction > 0) {
      dynamicSeatCountArr[itemIndex] = {
        ...currentItem,
        seatCount: currentItem.seatCount - actualReduction,
      };
      overflowSeatCount -= actualReduction;
    }
  }

  let updatedTotalSeatCount = dynamicSeatCountArr.reduce((acc, curr) => {
    return acc + curr.seatCount;
  }, 0);

  if (updatedTotalSeatCount < totalSeatCount) {
    dispatchCustomEvent(customEvents.openGameFilter, {
      name: "dynamic_seat_count",
    });
  }

  return dynamicSeatCountArr;
}

export function calcUniqueOpponents(opponents: OpponentType[]) {
  return opponents
    .filter(
      (itm, idx, arr) =>
        arr.findIndex((itm2) => itm2.opponentName === itm.opponentName) === idx
    )
    .map((itm) => {
      return {
        ...itm,
        opponentName: itm?.opponentName || "",
        opponentUrl: itm?.opponentUrl || "",
      };
    })
    .sort((a, b) => a?.opponentName?.localeCompare(b?.opponentName));
}

export function filterOpponentsCategoryBased(args: {
  selectedCategories: string[];
  events: EventType[];
  opponents: OpponentType[];
}): OpponentType[] {
  const { selectedCategories, events, opponents } = args;
  if (selectedCategories?.length > 0 && events?.length > 0) {
    const updatedOpponents = calcUniqueOpponents(
      events
        .filter(
          (itm) => itm.categories && selectedCategories.includes(itm.categories)
        )
        .map((itm) => ({
          opponentName: itm.opponentName,
          opponentUrl: itm.logoUrl,
        }))
    );
    return updatedOpponents;
  } else {
    return opponents;
  }
}

export function isFiltersApplied(gameFilters: GameFilters) {
  const { opponents, dates } = gameFilters;
  if (opponents?.selected?.length > 0) return true;
  if (dates?.length > 0) return true;
  return false;
}
